import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { Close, Explore, Group, KeyboardArrowDown, MeetingRoom, Search, AllInclusive } from '@material-ui/icons';
import clsx from 'clsx';
import { Dungeons, Raids, getPveInstance, IPveInstance } from '@pvplevelup/core/dist/interfaces/Pve/IPveInstance';
import { PveIcons } from 'utilities/pve_icons';

const ACCENT = '#42a5f5';
const RAID = '#ffa726';
const MUTED = 'rgba(255, 255, 255, 0.6)';
const DIVIDER = '1px solid rgba(255, 255, 255, 0.07)';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: 760,
    maxWidth: 'calc(100vw - 48px)',
    height: 'min(640px, calc(100vh - 64px))',
    display: 'grid',
    gridTemplateRows: 'auto auto minmax(0, 1fr)',
    overflow: 'hidden',
    borderRadius: 14,
    background: 'linear-gradient(180deg, #202127, #16171b)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.6)',
  },
  dialogHeader: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: 14,
    alignItems: 'center',
    padding: theme.spacing(2, 2.5),
    borderBottom: DIVIDER,
  },
  dialogTitle: {
    fontSize: '1.15rem',
    fontWeight: 800,
  },
  dialogSubtitle: {
    marginTop: 1,
    fontSize: '0.8rem',
    color: MUTED,
  },
  dialogTools: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: DIVIDER,
    background: 'rgba(255, 255, 255, 0.015)',
  },
  segmented: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 4,
    padding: 4,
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  segment: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 7,
    alignItems: 'center',
    height: 34,
    padding: '0 14px',
    borderRadius: 7,
    fontWeight: 700,
    fontSize: '0.88rem',
    color: MUTED,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': {
      color: '#fff',
    },
    '& svg': {
      fontSize: 18,
    },
  },
  segmentActive: {
    color: '#fff',
    background: 'rgba(66, 165, 245, 0.18)',
    boxShadow: `inset 0 -2px 0 ${ACCENT}`,
  },
  segmentCount: {
    padding: '0 6px',
    borderRadius: 8,
    fontSize: '0.72rem',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  search: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: 'rgba(0, 0, 0, 0.25)',
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gridAutoRows: 'max-content',
    gridGap: theme.spacing(1.25),
    padding: theme.spacing(2, 2.5),
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      background: 'rgba(255, 255, 255, 0.15)',
    },
  },
  card: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 12,
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.07)',
    transition: 'background 0.12s, border-color 0.12s, transform 0.12s',
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.08)',
      borderColor: 'rgba(66, 165, 245, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  cardActive: {
    background: 'rgba(66, 165, 245, 0.14)',
    borderColor: ACCENT,
    boxShadow: `0 0 0 1px ${ACCENT} inset`,
  },
  allCard: {
    gridColumn: '1 / -1',
  },
  icon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    color: ACCENT,
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  iconSmall: {
    width: 34,
    height: 34,
    borderRadius: 8,
    '& svg': {
      fontSize: 19,
    },
  },
  iconRaid: {
    color: RAID,
    background: 'rgba(255, 167, 38, 0.14)',
    borderColor: 'rgba(255, 167, 38, 0.35)',
  },
  name: {
    fontWeight: 700,
    fontSize: '0.9rem',
    lineHeight: 1.25,
  },
  meta: {
    marginTop: 2,
    fontSize: '0.78rem',
    color: MUTED,
  },
  warnTag: {
    marginLeft: 6,
    padding: '0 6px',
    borderRadius: 6,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#ffcc80',
    background: 'rgba(255, 167, 38, 0.14)',
  },
  empty: {
    gridColumn: '1 / -1',
    padding: theme.spacing(2, 1),
    textAlign: 'center',
    fontSize: '0.88rem',
    color: MUTED,
  },
  // the button that opens the picker, for filter rows
  trigger: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: 10,
    alignItems: 'center',
    height: 40,
    minWidth: 240,
    maxWidth: 340,
    boxSizing: 'border-box',
    padding: '0 8px 0 5px',
    borderRadius: 10,
    cursor: 'pointer',
    userSelect: 'none',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.25)',
      background: 'rgba(0, 0, 0, 0.4)',
    },
  },
  triggerSet: {
    borderColor: 'rgba(66, 165, 245, 0.5)',
  },
  triggerIcon: {
    width: 30,
    height: 30,
    borderRadius: 7,
    display: 'grid',
    placeItems: 'center',
    color: ACCENT,
    background: 'rgba(66, 165, 245, 0.14)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '& svg': {
      fontSize: 17,
    },
  },
  triggerLabel: {
    fontSize: '0.65rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
    lineHeight: 1.2,
  },
  triggerValue: {
    fontSize: '0.88rem',
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

export const isRaidInstance = (instance: IPveInstance) => Raids.some((r) => r.id == instance.id);

export const instanceMeta = (instance: IPveInstance) => {
  const bosses = instance.encounters.length;
  const size = instance.solo ? 'Solo' : `Up to ${instance.maxPlayers} players`;
  return {bosses: bosses == 0 ? null : `${bosses} ${bosses == 1 ? 'boss' : 'bosses'}`, size};
}

/** A dungeon or raid's icon tile: its artwork when there is one, otherwise a door (dungeon) or group (raid). */
export const InstanceIcon: React.FC<{instance: IPveInstance, small?: boolean, artwork?: boolean}> = ({instance, small, artwork}) => {
  const classes = useStyles();
  const raid = isRaidInstance(instance);
  const image = artwork ? PveIcons[instance.id] : null;
  return (
    <div className={clsx(classes.icon, {[classes.iconRaid]: raid && !image, [classes.iconSmall]: small})} style={image ? {backgroundImage: `url(${image})`} : undefined}>
      {!image && (raid ? <Group /> : <MeetingRoom />)}
    </div>
  );
}

type InstanceKind = 'dungeon' | 'raid';

interface PickerProps {
  open: boolean;
  selectedId: number | '' | null;
  onClose: () => void;
  /** null when "All dungeons & raids" is picked (only offered with allowAll) */
  onSelect: (instance: IPveInstance | null) => void;
  /** which instances to offer; every dungeon and raid by default */
  instances?: IPveInstance[];
  /** offer "All dungeons & raids" (for filters) */
  allowAll?: boolean;
  title?: string;
  subtitle?: string;
  /** flag instances with no bosses entered (the Boss Explorer cares; result pages only list ready ones) */
  showMissingBosses?: boolean;
}

/** Pick a dungeon or raid: Dungeons / Raids, a search, and a card per instance. */
export const EncounterPicker: React.FC<PickerProps> = ({open, selectedId, onClose, onSelect, instances, allowAll, title = 'Select PvE Encounter', subtitle = 'Choose a dungeon or raid.', showMissingBosses}) => {
  const classes = useStyles();
  const selected = selectedId != null && selectedId !== '' ? getPveInstance(selectedId) : null;
  const [kind, setKind] = useState<InstanceKind>(selected && isRaidInstance(selected) ? 'raid' : 'dungeon');
  const [search, setSearch] = useState('');
  const offered = instances || Dungeons.concat(Raids);
  const dungeons = offered.filter((i) => !isRaidInstance(i));
  const raids = offered.filter((i) => isRaidInstance(i));

  // open on the kind of the current pick
  useEffect(() => {
    if(open){
      setSearch('');
      if(selected)
        setKind(isRaidInstance(selected) ? 'raid' : 'dungeon');
    }
  }, [open]);

  const term = search.toLowerCase();
  const list = (kind == 'raid' ? raids : dungeons)
    .filter((i) => !term || i.name.toLowerCase().includes(term) || (i.zoneName || '').toLowerCase().includes(term))
    .slice().sort((a, b) => a.name.localeCompare(b.name));

  const segment = (value: InstanceKind, label: string, Icon: React.ElementType, count: number) => (
    <div className={clsx(classes.segment, {[classes.segmentActive]: kind == value})} onClick={() => setKind(value)}>
      <Icon />
      <span>{label}</span>
      <span className={classes.segmentCount}>{count}</span>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{className: classes.dialogPaper}}>
      <div className={classes.dialogHeader}>
        <div className={classes.icon}><Explore /></div>
        <div>
          <div className={classes.dialogTitle}>{title}</div>
          <div className={classes.dialogSubtitle}>{subtitle}</div>
        </div>
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </div>
      <div className={classes.dialogTools}>
        <div className={classes.segmented}>
          {segment('dungeon', 'Dungeons', MeetingRoom, dungeons.length)}
          {segment('raid', 'Raids', Group, raids.length)}
        </div>
        <TextField className={classes.search} size="small" variant="outlined" placeholder={`Search ${kind == 'raid' ? 'raids' : 'dungeons'}`} value={search}
          onChange={(e) => setSearch(e.target.value)} autoFocus
          InputProps={{startAdornment: <InputAdornment position="start"><Search style={{color: MUTED}} /></InputAdornment>}} />
      </div>
      <div className={classes.grid}>
        {allowAll && !term &&
          <div className={clsx(classes.card, classes.allCard, {[classes.cardActive]: selected == null})} onClick={() => onSelect(null)}>
            <div className={clsx(classes.icon, classes.iconSmall)}><AllInclusive /></div>
            <div>
              <div className={classes.name}>All dungeons &amp; raids</div>
              <div className={classes.meta}>Don't filter by instance</div>
            </div>
          </div>
        }
        {list.length == 0 && <div className={classes.empty}>{term ? `Nothing matches "${search}".` : `No ${kind == 'raid' ? 'raids' : 'dungeons'} here yet.`}</div>}
        {list.map((instance) => {
          const meta = instanceMeta(instance);
          return (
            <div key={instance.id} className={clsx(classes.card, {[classes.cardActive]: selected != null && instance.id === selected.id})}
              onClick={() => onSelect(instance)} title={instance.zoneName}>
              <InstanceIcon instance={instance} small artwork />
              <div style={{minWidth: 0}}>
                <div className={classes.name}>{instance.name}</div>
                <div className={classes.meta}>
                  {meta.size}
                  {meta.bosses ? ` · ${meta.bosses}` : showMissingBosses ? <span className={classes.warnTag}>no bosses</span> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

interface ButtonProps {
  selectedId: number | '' | null;
  onClick: () => void;
  label?: string;
}

/** A filter-row control showing the picked dungeon / raid (or "All"); opens the EncounterPicker. */
export const EncounterPickerButton: React.FC<ButtonProps> = ({selectedId, onClick, label = 'Dungeon / Raid'}) => {
  const classes = useStyles();
  const selected = selectedId != null && selectedId !== '' ? getPveInstance(selectedId) : null;
  const image = selected ? PveIcons[selected.id] : null;
  return (
    <div role="button" className={clsx(classes.trigger, {[classes.triggerSet]: selected != null})} onClick={onClick}>
      <div className={classes.triggerIcon} style={image ? {backgroundImage: `url(${image})`} : undefined}>
        {!image && (selected ? (isRaidInstance(selected) ? <Group /> : <MeetingRoom />) : <AllInclusive />)}
      </div>
      <div style={{minWidth: 0}}>
        <div className={classes.triggerLabel}>{label}</div>
        <div className={classes.triggerValue}>{selected ? selected.name : 'All dungeons & raids'}</div>
      </div>
      <KeyboardArrowDown style={{color: MUTED}} />
    </div>
  );
}
